// Derive a JSON Schema from an existing item's metadata.
//
// This is how a dataset's `metadata_schema` can be bootstrapped from real data: the inferred
// schema mirrors the observed shape (types, nested objects, array items) and marks the observed
// keys as required.

const isPlainObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const schemaFor = (value) => {
  if (value === null) return { type: "null" };

  if (Array.isArray(value)) {
    const itemSchemas = value.map(schemaFor);
    return { type: "array", items: itemSchemas.length > 0 ? mergeSchemas(itemSchemas) : {} };
  }

  if (isPlainObject(value)) {
    const properties = {};
    Object.entries(value).forEach(([key, entry]) => {
      properties[key] = schemaFor(entry);
    });

    return { type: "object", properties, required: Object.keys(value) };
  }

  if (typeof value === "number") return Number.isInteger(value) ? { type: "integer" } : { type: "number" };
  if (typeof value === "boolean") return { type: "boolean" };

  return { type: "string" };
};

/** Combine schemas observed for several values into one (anyOf when they disagree). */
export const mergeSchemas = (schemas = []) => {
  const unique = [];

  schemas.forEach((schema) => {
    const key = JSON.stringify(schema);
    if (!unique.some((entry) => JSON.stringify(entry) === key)) unique.push(schema);
  });

  if (unique.length === 0) return {};
  if (unique.length === 1) return unique[0];

  return { anyOf: unique };
};

/**
 * Infer a JSON Schema for a metadata value.
 *
 * @param {object|array|string|null} metadata A value, or its JSON text.
 * @returns {object} A JSON Schema object.
 */
export const inferSchema = (metadata) => {
  let value = metadata;

  if (typeof value === "string") {
    value = value.trim() === "" ? {} : JSON.parse(value);
  }

  if (value === null || value === undefined) return { type: "object", properties: {} };

  const schema = schemaFor(value);

  if (schema.type === "object") {
    return {
      type: "object",
      properties: schema.properties ?? {},
      ...(schema.required?.length ? { required: schema.required } : {}),
    };
  }

  return schema;
};

export default inferSchema;
